import React, { useEffect, useState } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { trackCTA } from '../lib/tracking';

/**
 * Botón flotante de WhatsApp.
 * - Aparece tras 5s desde la primera interacción de scroll (mejor que onload).
 * - Pulso suave para llamar la atención sin ser invasivo.
 * - Dismissible: si el usuario lo cierra, no vuelve a aparecer en esta sesión.
 * - Pre-llena un mensaje útil hacia el equipo PreventiApp.
 */
const WHATSAPP_NUMBER = '56996099788';
const PREFILLED_MSG =
  'Hola PreventiApp, me interesa conocer más sobre la plataforma para mi colegio.';

const WhatsAppFloat = () => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem('wa_dismissed') === '1') {
      setDismissed(true);
      return;
    }

    let timer = null;
    let scrolled = false;

    const arm = () => {
      if (scrolled || timer) return;
      scrolled = true;
      timer = setTimeout(() => setVisible(true), 5000);
    };

    // Activar al primer scroll real
    window.addEventListener('scroll', arm, { passive: true, once: true });
    // Fallback: si nunca hace scroll, aparece a los 12s
    const fallback = setTimeout(() => setVisible(true), 12000);

    return () => {
      window.removeEventListener('scroll', arm);
      if (timer) clearTimeout(timer);
      clearTimeout(fallback);
    };
  }, []);

  if (dismissed) return null;

  const onClick = () => {
    trackCTA('whatsapp-floating', { location: 'floating', label: 'WhatsApp flotante' });
  };

  const onClose = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    setDismissed(true);
    try {
      sessionStorage.setItem('wa_dismissed', '1');
    } catch (e) {
      // no-op
    }
  };

  return (
    <div
      data-testid="whatsapp-float"
      className={`fixed bottom-5 right-5 sm:bottom-7 sm:right-7 z-50 transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
      aria-hidden={!visible}
    >
      <div className="relative group">
        {/* Pulso suave */}
        <span
          aria-hidden
          className="absolute inset-0 rounded-full animate-ping"
          style={{ background: 'rgba(37, 211, 102, 0.35)', animationDuration: '2.8s' }}
        />
        {/* Botón cerrar */}
        <button
          data-testid="whatsapp-float-close"
          onClick={onClose}
          aria-label="Cerrar WhatsApp flotante"
          className="tap-44 absolute -top-2 -left-2 z-20 w-11 h-11 rounded-full bg-white border border-brand text-brand-deep flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity hover:text-brand-violet"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Tooltip */}
        <div className="hidden sm:block absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-white border border-brand px-4 py-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          <span className="text-sm font-semibold text-brand-deep">Conversemos por WhatsApp</span>
          <span className="block text-xs text-brand-muted">+56 9 9609 9788 · Lun a Vie</span>
        </div>

        {/* Botón principal */}
        <a
          data-testid="whatsapp-float-cta"
          href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(PREFILLED_MSG)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClick}
          aria-label="Chatear por WhatsApp"
          className="relative z-10 inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full text-white shadow-[0_20px_40px_-15px_rgba(37,211,102,0.7)] hover:scale-105 active:scale-100 transition-transform"
          style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
        >
          <MessageCircle className="w-7 h-7 sm:w-8 sm:h-8" />
        </a>
      </div>
    </div>
  );
};

export default WhatsAppFloat;
